import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, Pencil, Save, Trash2 } from 'lucide-react';
import { api } from '../../services/api';
import senerfiFormato from '../../assets/images/Senerfi-formato.png';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

const IS_SIG_MOBILE = String(process.env.REACT_APP_APP_VARIANT || '').trim().toLowerCase() === 'sig_mobile';

const MONTH_OPTIONS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const FORM_CODE = 'FT-AMB-095';
const FORM_VERSION = '0';

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function monthFromDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const idx = Number(raw.slice(5, 7)) - 1;
  return MONTH_OPTIONS[idx] || '';
}

function yearFromDate(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  return raw.slice(0, 4);
}

function emptyForm(locationLabel = '') {
  const date = todayYmd();
  return {
    id: '',
    service_date: date,
    service_year: yearFromDate(date),
    service_month: monthFromDate(date),
    site_name: locationLabel,
    consumption_type: '',
    consumption_value: '',
    staff_count: '',
    per_capita_consumption: '',
    goal_value: '',
    invoice_info: '',
    manager_name: '',
    support_info: '',
    support_file: null,
    existing_support_file_url: '',
    existing_support_file_name: '',
    existing_support_file_path: '',
    notes: '',
  };
}

function fmtDateDisplay(value) {
  const raw = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || '-';
  return `${raw.slice(8, 10)}/${raw.slice(5, 7)}/${raw.slice(0, 4)}`;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

async function assetToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function ControlServiciosAmbientalModule({ location }) {
  const title = useMemo(() => {
    if (location === 'popayan') return 'Popayán';
    if (location === 'santander-de-quilichao') return 'Santander de Quilichao';
    return location || 'Sede';
  }, [location]);

  const [form, setForm] = useState(() => emptyForm(title));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    setForm((prev) => (prev.id ? prev : { ...emptyForm(title) }));
  }, [title]);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.sgi.services.list({ location, limit: 1000, order: 'desc' });
      const data = Array.isArray(res?.data) ? res.data : [];
      setRows(data);
    } catch (err) {
      console.error('Error cargando control de servicios', err);
      setFeedback('No se pudo cargar la información de control de servicios.');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const onDateChange = (value) => {
    setForm((prev) => ({
      ...prev,
      service_date: value,
      service_month: monthFromDate(value) || prev.service_month,
      service_year: yearFromDate(value) || prev.service_year,
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.service_date || !form.site_name.trim() || !form.consumption_type.trim()) {
      setFeedback('Completa fecha, sede y tipo de consumo.');
      return;
    }
    try {
      setSaving(true);
      setFeedback('');
      await api.sgi.services.upsert({
        ...form,
        location,
      });
      setForm(emptyForm(title));
      setFeedback('Registro guardado correctamente.');
      await loadRows();
    } catch (err) {
      console.error('Error guardando control de servicios', err);
      setFeedback(err?.message || 'No se pudo guardar el registro.');
    } finally {
      setSaving(false);
    }
  };

  const onEdit = (row) => {
    setForm({
      id: row.id || '',
      service_date: row.service_date || '',
      service_year: row.service_year || '',
      service_month: row.service_month || '',
      site_name: row.site_name || title,
      consumption_type: row.consumption_type || '',
      consumption_value: row.consumption_value ?? '',
      staff_count: row.staff_count ?? '',
      per_capita_consumption: row.per_capita_consumption ?? '',
      goal_value: row.goal_value || '',
      invoice_info: row.invoice_info || '',
      manager_name: row.manager_name || '',
      support_info: row.support_info || '',
      support_file: null,
      existing_support_file_url: row.support_file_url || '',
      existing_support_file_name: row.support_file_name || '',
      existing_support_file_path: row.support_file_path || '',
      notes: row.notes || '',
    });
    setFeedback(`Editando registro #${row.id}.`);
  };

  const onDelete = async (row) => {
    const ok = window.confirm(`¿Eliminar el registro de control de servicios #${row.id}?`);
    if (!ok) return;
    try {
      await api.sgi.services.delete({ id: row.id });
      setFeedback('Registro eliminado.');
      if (String(form.id) === String(row.id)) setForm(emptyForm(title));
      await loadRows();
    } catch (err) {
      console.error('Error eliminando control de servicios', err);
      setFeedback(err?.message || 'No se pudo eliminar el registro.');
    }
  };

  const onCancelEdit = () => {
    setForm(emptyForm(title));
    setFeedback('');
  };

  const exportExcel = async () => {
    try {
      const logoDataUrl = await assetToDataUrl(senerfiFormato);
      const bodyRows = rows.map((row) => `
        <tr>
          <td>${escapeHtml(fmtDateDisplay(row.service_date))}</td>
          <td>${escapeHtml(row.service_year || '')}</td>
          <td>${escapeHtml(row.service_month || '')}</td>
          <td>${escapeHtml(row.site_name || '')}</td>
          <td>${escapeHtml(row.consumption_type || '')}</td>
          <td>${escapeHtml(row.consumption_value ?? '')}</td>
          <td>${escapeHtml(row.staff_count ?? '')}</td>
          <td>${escapeHtml(row.per_capita_consumption ?? '')}</td>
          <td>${escapeHtml(row.goal_value || '')}</td>
          <td>${escapeHtml(row.invoice_info || '')}</td>
          <td>${escapeHtml(row.manager_name || '')}</td>
          <td>${escapeHtml(row.support_file_name || row.support_info || '')}</td>
        </tr>
      `).join('');

      const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office"
              xmlns:x="urn:schemas-microsoft-com:office:excel"
              xmlns="http://www.w3.org/TR/REC-html40">
          <head>
            <meta charset="UTF-8" />
            <!--[if gte mso 9]><xml>
              <x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet>
                <x:Name>Control servicios</x:Name>
                <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
              </x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook>
            </xml><![endif]-->
            <style>
              body { font-family: Arial, sans-serif; }
              table { border-collapse: collapse; width: 100%; table-layout: fixed; }
              td, th { border: 1px solid #000; padding: 6px 8px; font-size: 11pt; vertical-align: middle; }
              .title { text-align: center; font-weight: 700; }
              .meta-label { font-weight: 700; text-align: left; width: 150px; }
              .meta-value { text-align: right; width: 110px; }
              .header { background: #f4bf16; font-weight: 700; text-align: center; }
              .logo-cell { text-align: center; }
              .logo-cell img { max-width: 160px; max-height: 70px; }
              .w-fecha { width: 90px; }
              .w-ano { width: 70px; }
              .w-mes { width: 90px; }
              .w-sede { width: 130px; }
              .w-tipo { width: 150px; }
              .w-consumo { width: 130px; }
              .w-personas { width: 170px; }
              .w-percapita { width: 140px; }
              .w-meta { width: 90px; }
              .w-factura { width: 170px; }
              .w-gestor { width: 120px; }
              .w-soporte { width: 140px; }
            </style>
          </head>
          <body>
            <table>
              <colgroup>
                <col class="w-fecha" />
                <col class="w-ano" />
                <col class="w-mes" />
                <col class="w-sede" />
                <col class="w-tipo" />
                <col class="w-consumo" />
                <col class="w-personas" />
                <col class="w-percapita" />
                <col class="w-meta" />
                <col class="w-factura" />
                <col class="w-gestor" />
                <col class="w-soporte" />
              </colgroup>
              <tr>
                <td class="logo-cell" colspan="2" rowspan="3"><img src="${logoDataUrl}" alt="Senerfi" /></td>
                <td class="title" colspan="8">SOLUCIONES ENERGETICAS FORESTALES INTEGRALES SAS</td>
                <td class="meta-label" colspan="1">CÓDIGO:</td>
                <td class="meta-value" colspan="1">${escapeHtml(FORM_CODE)}</td>
              </tr>
              <tr>
                <td class="title" colspan="8">GESTIÓN AMBIENTAL</td>
                <td class="meta-label" colspan="1">VERSIÓN:</td>
                <td class="meta-value" colspan="1">${escapeHtml(FORM_VERSION)}</td>
              </tr>
              <tr>
                <td class="title" colspan="8">REPORTE DE CONSUMOS DE AGUA Y ENERGÍA</td>
                <td class="meta-label" colspan="1">FECHA:</td>
                <td class="meta-value" colspan="1">${escapeHtml(fmtDateDisplay(todayYmd()))}</td>
              </tr>
              <tr>
                <th class="header">FECHA</th>
                <th class="header">AÑO</th>
                <th class="header">MES</th>
                <th class="header">SEDE</th>
                <th class="header">TIPO DE CONSUMO</th>
                <th class="header">CONSUMO (M3, KWH)</th>
                <th class="header"># PERSONAS ADMINISTRATIVO Y FLOTANTE</th>
                <th class="header">CONSUMO PER CÁPITA</th>
                <th class="header">META</th>
                <th class="header"># INFORMACIÓN DE LA FACTURA</th>
                <th class="header">GESTOR</th>
                <th class="header">SOPORTE</th>
              </tr>
              ${bodyRows || '<tr><td colspan="12" style="text-align:center;">Sin registros</td></tr>'}
            </table>
          </body>
        </html>
      `;

      const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `control_servicios_${location || 'sede'}_${todayYmd()}.xls`;
      document.body.appendChild(link);
      void recordOperationsDownload({ fileType: 'xls', moduleKey: 'sgi', reportName: 'Control de servicios ambientales' });
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando control de servicios', err);
      setFeedback('No se pudo exportar el archivo de Excel con formato.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-base font-semibold text-gray-800">Control servicios</div>
          <div className="text-sm text-gray-600">Reporte de consumos de agua y energía en {title}.</div>
        </div>
        {!IS_SIG_MOBILE && (
          <button
            type="button"
            onClick={exportExcel}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            <Download size={16} /> Exportar Excel
          </button>
        )}
      </div>

      <form onSubmit={onSubmit} className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Fecha</label>
            <input
              type="date"
              className="w-full border rounded px-3 py-2"
              value={form.service_date}
              onChange={(e) => onDateChange(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Año</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2"
              value={form.service_year}
              onChange={(e) => setForm((prev) => ({ ...prev, service_year: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Mes</label>
            <select
              className="w-full border rounded px-3 py-2 bg-white"
              value={form.service_month}
              onChange={(e) => setForm((prev) => ({ ...prev, service_month: e.target.value }))}
            >
              <option value="">Seleccione...</option>
              {MONTH_OPTIONS.map((month) => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Sede</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.site_name}
              onChange={(e) => setForm((prev) => ({ ...prev, site_name: e.target.value }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Tipo de consumo</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.consumption_type}
              onChange={(e) => setForm((prev) => ({ ...prev, consumption_type: e.target.value }))}
              placeholder="Agua o energía"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Consumo (M3, KWH)</label>
            <input
              type="number"
              step="0.01"
              className="w-full border rounded px-3 py-2"
              value={form.consumption_value}
              onChange={(e) => setForm((prev) => ({ ...prev, consumption_value: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1"># personas administrativo y flotante</label>
            <input
              type="number"
              className="w-full border rounded px-3 py-2"
              value={form.staff_count}
              onChange={(e) => setForm((prev) => ({ ...prev, staff_count: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Consumo per cápita</label>
            <input
              type="number"
              step="0.0001"
              className="w-full border rounded px-3 py-2"
              value={form.per_capita_consumption}
              onChange={(e) => setForm((prev) => ({ ...prev, per_capita_consumption: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Meta</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.goal_value}
              onChange={(e) => setForm((prev) => ({ ...prev, goal_value: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1"># información de la factura</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.invoice_info}
              onChange={(e) => setForm((prev) => ({ ...prev, invoice_info: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Gestor</label>
            <input
              type="text"
              className="w-full border rounded px-3 py-2"
              value={form.manager_name}
              onChange={(e) => setForm((prev) => ({ ...prev, manager_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Soporte</label>
            <input
              type="file"
              className="w-full border rounded px-3 py-2"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx"
              onChange={(e) => setForm((prev) => ({ ...prev, support_file: e.target.files?.[0] || null }))}
            />
            <div className="mt-1 text-xs text-gray-500">Adjunta PDF, imagen o archivo Office.</div>
            {form.existing_support_file_url ? (
              <a
                href={form.existing_support_file_url}
                target="_blank"
                rel="noreferrer"
                className="mt-1 inline-block text-xs text-blue-700 hover:underline"
              >
                Ver soporte actual: {form.existing_support_file_name || 'Abrir archivo'}
              </a>
            ) : null}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Detalle del soporte / observaciones</label>
          <input
            type="text"
            className="w-full border rounded px-3 py-2"
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className={`text-sm ${feedback.includes('correctamente') || feedback.includes('eliminado') ? 'text-emerald-700' : 'text-yellow-700'}`}>
            {feedback || 'Registra y consulta el control de servicios por sede.'}
          </div>
          <div className="flex items-center gap-2">
            {form.id ? (
              <button
                type="button"
                onClick={onCancelEdit}
                className="px-4 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
              >
                Cancelar edición
              </button>
            ) : null}
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <Save size={16} /> {saving ? 'Guardando...' : (form.id ? 'Actualizar' : 'Guardar')}
            </button>
          </div>
        </div>
      </form>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
          <div className="font-medium text-gray-800">Registros guardados</div>
          <div className="text-sm text-gray-500">{loading ? 'Cargando...' : `${rows.length} registros`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-yellow-400 text-gray-900">
              <tr>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Año</th>
                <th className="px-3 py-2 text-left">Mes</th>
                <th className="px-3 py-2 text-left">Sede</th>
                <th className="px-3 py-2 text-left">Tipo de consumo</th>
                <th className="px-3 py-2 text-left">Consumo</th>
                <th className="px-3 py-2 text-left"># personas</th>
                <th className="px-3 py-2 text-left">Per cápita</th>
                <th className="px-3 py-2 text-left">Meta</th>
                <th className="px-3 py-2 text-left">Factura</th>
                <th className="px-3 py-2 text-left">Gestor</th>
                <th className="px-3 py-2 text-left">Soporte</th>
                <th className="px-3 py-2 text-left">Detalle</th>
                <th className="px-3 py-2 text-left">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={14} className="px-3 py-6 text-center text-gray-500">
                    No hay registros de control de servicios para esta sede.
                  </td>
                </tr>
              ) : rows.map((row) => (
                <tr key={row.id} className="border-t">
                  <td className="px-3 py-2">{fmtDateDisplay(row.service_date)}</td>
                  <td className="px-3 py-2">{row.service_year || '-'}</td>
                  <td className="px-3 py-2">{row.service_month || '-'}</td>
                  <td className="px-3 py-2">{row.site_name || '-'}</td>
                  <td className="px-3 py-2">{row.consumption_type || '-'}</td>
                  <td className="px-3 py-2">{row.consumption_value ?? '-'}</td>
                  <td className="px-3 py-2">{row.staff_count ?? '-'}</td>
                  <td className="px-3 py-2">{row.per_capita_consumption ?? '-'}</td>
                  <td className="px-3 py-2">{row.goal_value || '-'}</td>
                  <td className="px-3 py-2">{row.invoice_info || '-'}</td>
                  <td className="px-3 py-2">{row.manager_name || '-'}</td>
                  <td className="px-3 py-2">
                    {row.support_file_url ? (
                      <a href={row.support_file_url} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline">
                        {row.support_file_name || 'Abrir soporte'}
                      </a>
                    ) : '-'}
                  </td>
                  <td className="px-3 py-2">{row.support_info || '-'}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onEdit(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        <Pencil size={14} /> Editar
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(row)}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-red-200 text-red-700 hover:bg-red-50"
                      >
                        <Trash2 size={14} /> Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
